import { useState } from 'react';
import type { AdminApp, AdminAppDetail, StoreReadinessItem } from '@boardflight/shared';
import { AppGenre, STORE_READINESS_LABELS } from '@boardflight/shared';
import { api, put } from '../api.js';
import {
  Async,
  Filter,
  ListMore,
  Modal,
  Panel,
  Time,
  useAsync,
  useDebounced,
  useListLength,
  useSelection,
  when,
} from '../ui.js';

/**
 * Games, and the two things about a game this console may change: its package id (§D5.6) and
 * whether the store's readiness checklist applies to it (§D5.7).
 *
 * Not an analytics screen — the Analytics tab already ranks games by activity, and a game with no
 * activity does not appear there at all. This one finds any game by any of the four things
 * somebody might have in hand (name, slug, package id, id), including archived ones, because an
 * abandoned game holding a package id is exactly the row a rename is likeliest to be about.
 *
 * It is also where the **storefront is read from the outside**, which is the one view nobody else
 * has: a team sees its own game and a player sees a shelf, and neither can answer "what is actually
 * on the store, and what thinks it is". The Store column is that answer per row, `store=` narrows
 * to it, `genre=` narrows to a shelf, and the popularity sorts use the store's own ordering. None
 * of it is a control — there is no button on this screen that publishes, unpublishes or reorders
 * anything, and the two that do write are the two named above.
 */

interface GameFilter {
  q: string;
  /** A team's id, from `#games?team=<id>` — how the Teams tab links to a team's games. */
  team: string;
  platform: string;
  archived: string;
  genre: string;
  store: string;
  sort: string;
}

const EMPTY: GameFilter = { q: '', team: '', platform: '', archived: '', genre: '', store: '', sort: 'created' };

/** `team=` from the fragment's query, as the Teams tab writes it. */
const teamFromHash = (): string => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('team') ?? '';

export function Games() {
  const [filter, setFilter] = useState<GameFilter>(() => ({ ...EMPTY, team: teamFromHash() }));
  const [selected, setSelected] = useSelection('games');

  const q = useDebounced(filter.q);
  const [shown, showMore] = useListLength(
    [q, filter.team, filter.platform, filter.archived, filter.genre, filter.store, filter.sort].join('\n'),
  );

  const list = useAsync(
    () =>
      api<{ items: AdminApp[]; limit: number; total: number }>(
        `/v1/admin/apps?q=${encodeURIComponent(q)}&platform=${filter.platform}` +
          (filter.team ? `&team=${encodeURIComponent(filter.team)}` : '') +
          `&archived=${filter.archived}&genre=${filter.genre}&store=${filter.store}` +
          `&sort=${filter.sort}&limit=${shown}`,
      ),
    [q, filter.team, filter.platform, filter.archived, filter.genre, filter.store, filter.sort, shown],
  );

  const set = (patch: Partial<GameFilter>) => setFilter((current) => ({ ...current, ...patch }));
  const filtered =
    filter.q !== '' ||
    filter.team !== '' ||
    filter.platform !== '' ||
    filter.archived !== '' ||
    filter.genre !== '' ||
    filter.store !== '';

  return (
    <>
      <Panel
        title="Games"
        actions={
          <div className="filters">
            <label className="filter">
              <span className="muted small">Search</span>
              <input
                type="text"
                value={filter.q}
                placeholder="Name, slug, package id or id"
                onChange={(e) => set({ q: e.target.value })}
              />
            </label>
            <Filter
              label="Platform"
              value={filter.platform}
              onChange={(platform) => set({ platform })}
              options={[
                { value: '', label: 'Any platform' },
                { value: 'web', label: 'web' },
                { value: 'unity', label: 'unity' },
              ]}
            />
            {/* Archived games are in the list by default and always have been — they hold package
                ids, which is what this screen is for. The filter is how somebody asks for only the
                live ones, or only the abandoned ones, not how they get the default back. */}
            <Filter
              label="Archived"
              value={filter.archived}
              onChange={(archived) => set({ archived })}
              options={[
                { value: '', label: 'Included' },
                { value: 'exclude', label: 'Hidden' },
                { value: 'only', label: 'Only archived' },
              ]}
            />
            {/* Where a game stands with the storefront. `Published, dark` is the one worth having a
                filter for: from the team's side it looks exactly like a successful publish. */}
            <Filter
              label="Library"
              value={filter.store}
              onChange={(store) => set({ store })}
              options={[
                { value: '', label: 'Any' },
                { value: 'visible', label: 'In the library' },
                { value: 'dark', label: 'Published, dark' },
                { value: 'review', label: 'Awaiting approval' },
                { value: 'unpublished', label: 'Not published' },
              ]}
            />
            {/* The shelves, in the vocabulary's own order — the same list a developer picks from,
                so a word added to `AppGenre` appears here without a change to this file. */}
            <Filter
              label="Genre"
              value={filter.genre}
              onChange={(genre) => set({ genre })}
              options={[
                { value: '', label: 'Any genre' },
                ...AppGenre.options.map((genre) => ({ value: genre, label: genre })),
              ]}
            />
            <Filter
              label="Sort"
              value={filter.sort}
              onChange={(sort) => set({ sort })}
              options={[
                { value: 'created', label: 'Newest' },
                { value: 'name', label: 'Name' },
                { value: 'team', label: 'Team' },
                { value: 'package', label: 'Package id' },
                { value: 'builds', label: 'Most builds' },
                // The store's own Popular order, not a lookalike — see `CATALOG_POPULAR_ORDER`.
                { value: 'popular', label: 'Popular (7d)' },
                { value: 'plays', label: 'Most played (all time)' },
                { value: 'installs', label: 'Most installed' },
              ]}
            />
            {filter.team ? (
              <span className="pill">
                {list.data?.items[0]?.teamName ?? 'One team'}{' '}
                <button className="link" aria-label="Every team" onClick={() => set({ team: '' })}>×</button>
              </span>
            ) : null}
            {filtered ? <button onClick={() => setFilter(EMPTY)}>Clear</button> : null}
          </div>
        }
      >
        <Async state={list} empty="No games.">
          {(data) =>
            data.items.length === 0 ? (
              <p className="muted">{filtered ? 'No game matches this filter.' : 'No games.'}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Game</th>
                      <th>Library</th>
                      <th>Team</th>
                      <th>Platform</th>
                      <th>Package id</th>
                      <th className="num" title="Plays in the last seven days — what the library ranks by">
                        Plays 7d
                      </th>
                      <th className="num">Builds</th>
                      <th>Created</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {row.name} {row.archived ? <span className="pill">archived</span> : null}
                          <div className="muted mono">{row.slug}</div>
                        </td>
                        <td>
                          <StorePill app={row} />
                          {row.genres.length > 0 ? (
                            <div className="muted small">{row.genres.join(' · ')}</div>
                          ) : null}
                        </td>
                        <td><a href={`#teams/${row.teamId}`}>{row.teamName}</a></td>
                        <td className="muted">{row.platform}</td>
                        <td className="mono">{row.packageName}</td>
                        <td
                          className="num"
                          title={`${row.stats.playsAll} plays all time`}
                        >
                          {row.stats.plays7d}
                        </td>
                        <td className="num">{row.readyBuilds}</td>
                        <td className="muted">
                          <Time at={row.createdAt} />
                        </td>
                        <td>
                          <button onClick={() => setSelected(row.id)}>Open</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Async>
        <ListMore state={list} shown={shown} noun="games" onMore={showMore} />
      </Panel>

      {selected ? <Game appId={selected} onClose={() => setSelected(null)} /> : null}
    </>
  );
}

/**
 * Where one game stands with the storefront, in a pill.
 *
 * **Three states, not two**, and the middle one is the entire reason this exists. "Published" is
 * the fact a screen reaches for first and it is the wrong one: the catalog also drops an archived
 * game and a game with nothing installable on `public`, so a team can press Publish, get a success,
 * and reach nobody. From their side that is indistinguishable from working.
 *
 * So a dark row says **which** of the three it is, because they are three different next actions:
 * archiving is the team's own doing and probably deliberate; an empty `public` channel means
 * somebody has to promote a build; builds waiting on verification means nobody has to do anything
 * except wait for a runner. Guessing wrong here costs somebody an afternoon.
 *
 * `visible` is resolved by the Worker from the same module the storefront's query is built out of,
 * and is never recomputed in the browser — a pill that agreed with the catalog only most of the
 * time would be worse than no pill.
 */
function StorePill({ app }: { app: AdminApp }) {
  const { visible, publishedAt, publicBuilds, visibleBuilds } = app.store;

  if (visible) {
    return (
      <span className="pill on" title={`In the library since ${when(publishedAt)}.`}>
        in the library
      </span>
    );
  }

  if (publishedAt === null) {
    // Not a failure and not worth a colour: most games are never meant for the store at all.
    return <span className="muted">not published</span>;
  }

  // Asked, and not yet approved (docs/store-review-plan.md). Waiting is the Reviews tab's business;
  // sent back is the team's.
  if (app.store.approvedAt === null) {
    return app.store.pendingSince !== null ? (
      <a className="pill" href={`#reviews/${app.id}`} title={`Submitted ${when(app.store.pendingSince)}.`}>
        in review
      </a>
    ) : (
      <span className="pill" title="Changes were requested; waiting for the team to resubmit.">
        changes requested
      </span>
    );
  }

  const why = app.archived
    ? 'this game is archived, and the catalog drops an archived game without unpublishing it'
    : publicBuilds === 0
      ? 'nothing is on its `public` channel, so there is no build behind the listing'
      : `all ${publicBuilds} build${publicBuilds === 1 ? '' : 's'} on \`public\` are still waiting ` +
        'on hardware validation';

  return (
    <span
      className="pill off"
      title={`Published ${when(publishedAt)}, but not on any shelf: ${why}. (${visibleBuilds} of ${publicBuilds} builds on \`public\` are showable.)`}
    >
      published, dark
    </span>
  );
}

/** A web game's second identity, which the rename resets where it lives (`routes/apps.ts`). */
const WEB_RESET =
  'The Board appId resets too: the playground’s next fresh sandbox drops the old one, and the ' +
  'first deploy after that mints a new one — the game installs as a new app, with no saved ' +
  'games. A build packed outside the playground keeps the appId in its own board.config.json.';

/**
 * One game, and the rename — in a modal.
 *
 * The modal matters more here than on the other two screens: this is the console's heaviest write,
 * it is guarded by a `window.confirm` that spells out what a rename leaves behind, and everything
 * that decides whether it will be accepted — the team's prefixes, the count of finished builds —
 * has to be in front of the person while they type. A dialog is what puts it there instead of
 * somewhere below a table.
 *
 * Three things this screen owes the person using it, in the order they need them:
 *
 *  1. **What the team may publish.** The prefixes decide the answer, and showing them after a
 *     refusal is showing them too late. §4 is not relaxed for an admin — a game that has to move
 *     outside them needs a prefix claimed for that team first.
 *  2. **What the rename leaves behind.** Every finished build installs as the id its bytes
 *     declare, so a Board holding one has the game under the old name and will not recognise a
 *     build published under the new one as the same app. The count is stated, not implied. A web
 *     game's `appId` is reset too, by its playground's next sandbox, and the dialog says so.
 *  3. **Why.** The note is required by the route for the reason the identity reveal's is: this is
 *     one of the few acts here whose justification exists nowhere else.
 */
function Game({ appId, onClose }: { appId: string; onClose: () => void }) {
  const detail = useAsync(() => api<AdminAppDetail>(`/v1/admin/apps/${appId}`), [appId]);
  const [packageName, setPackageName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function rename(current: string, readyBuilds: number, platform: string) {
    setError(null);
    setDone(null);
    const next = packageName.trim();
    if (
      !window.confirm(
        `Rename ${current} to ${next}?\n\n` +
          (readyBuilds > 0
            ? `${readyBuilds} finished build${readyBuilds === 1 ? '' : 's'} still install as ` +
              `"${current}" and will not be recognised as this game once it publishes under the ` +
              `new id. Anyone who already has it installed keeps the old one.`
            : 'This game has no finished builds, so nothing is already installed under the old id.') +
          (platform === 'web' ? `\n\n${WEB_RESET}` : ''),
      )
    ) {
      return;
    }

    setBusy(true);
    try {
      await put(`/v1/admin/apps/${appId}/package-name`, { packageName: next, note: note.trim() });
      setPackageName('');
      setNote('');
      setDone(`Now publishing as ${next}.`);
      detail.reload();
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={detail.data?.app.name ?? 'Game'} onClose={onClose}>
      <Async state={detail}>
        {(data) => (
          <div className="stats">
            <div className="stat"><span className="stat-label">Plays 7d</span><span className="stat-value">{data.app.stats.plays7d}</span></div>
            <div className="stat"><span className="stat-label">Plays all time</span><span className="stat-value">{data.app.stats.playsAll}</span></div>
            <div className="stat"><span className="stat-label">Installs 7d</span><span className="stat-value">{data.app.stats.installs7d}</span></div>
            <div className="stat"><span className="stat-label">Installs all time</span><span className="stat-value">{data.app.stats.installsAll}</span></div>
            <div className="stat"><span className="stat-label">Finished builds</span><span className="stat-value">{data.app.readyBuilds}</span></div>
            <div className="stat">
              <span className="stat-label">Numbers</span>
              <span className="stat-value small"><a href={`#analytics/${data.app.id}`}>Analytics</a></span>
            </div>
          </div>
        )}
      </Async>
      <Async state={detail}>
        {(data) => (
          <Panel title="Package id">
            <div className="row">
              <span className="mono">{data.app.packageName}</span>
              <span className="muted">·</span>
              <span className="muted">
                {data.app.platform} · <a href={`#teams/${data.app.teamId}`}>{data.app.teamName}</a>
              </span>
              {data.app.archived ? <span className="pill">archived</span> : null}
              <span className="muted">·</span>
              <span className="muted">
                created <Time at={data.app.createdAt} />
              </span>
            </div>

            <div className="row">
              <label htmlFor="package">New package id</label>
              <input
                id="package"
                type="text"
                className="mono"
                style={{ minWidth: 280 }}
                placeholder={data.app.packageName}
                value={packageName}
                onChange={(e) => setPackageName(e.target.value)}
              />
              <input
                type="text"
                style={{ minWidth: 240 }}
                placeholder="Why (required, kept in the audit log)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <button
                className="danger"
                disabled={busy || packageName.trim() === '' || note.trim() === ''}
                onClick={() => void rename(data.app.packageName, data.app.readyBuilds, data.app.platform)}
              >
                {busy ? 'Renaming…' : 'Rename'}
              </button>
            </div>

            <p className="note">
              {data.prefixes.length > 0 ? (
                <>
                  Must start with one of {data.app.teamName}&rsquo;s prefixes:{' '}
                  <span className="mono">{data.prefixes.join(', ')}</span>.
                </>
              ) : (
                <span className="warn">
                  {data.app.teamName} has claimed no package prefixes, so no rename can be accepted.
                  Claim one for that team first.
                </span>
              )}
            </p>

            {/* What a rename leaves behind — finished builds still installing as the old id, a web
                game's appId reset — is said in the confirmation, where it is read at the moment it
                matters, rather than in two more paragraphs here. */}
            {error ? <p className="error">{error}</p> : null}
            {done ? <p className="ok">{done}</p> : null}
          </Panel>
        )}
      </Async>

      <Async state={detail}>{(data) => <StoreWaiver app={data.app} onChanged={detail.reload} />}</Async>
    </Modal>
  );
}

/**
 * The store's readiness checklist, and the operator's exception to it (§D5.7).
 *
 * **This panel cannot publish anything and there is no button here that does.** Publishing reaches
 * everybody under the team's name, which is why the public API asks for `owner`; an operator doing
 * it on their behalf would put a team's name on a storefront listing nobody in it pressed anything
 * to create. What this does is remove one refusal from in front of the team's own button.
 *
 * So the panel has to say three things before it is used, because the waiver is worth granting only
 * when all three line up:
 *
 *  1. **What is actually missing** — the rule being waived, in the same words the team's dashboard
 *     shows, from the same function the publish route enforces.
 *  2. **Whether it is already published** — a waiver changes nothing today for a game already on
 *     the store; it is the *next* publish it matters for.
 *  3. **Whether there is anything to show.** The catalog lists a published game only if it has a
 *     finished build on `public`. A waiver granted to a game with none reaches nobody, and that is
 *     better learnt here than after somebody asks why the store is still empty.
 */
function StoreWaiver({ app, onChanged }: { app: AdminApp; onChanged: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const waived = app.store.readinessWaivedAt !== null;
  const published = app.store.publishedAt !== null;
  const missing = app.store.missing;

  async function set(on: boolean) {
    setError(null);
    setDone(null);
    setBusy(true);
    try {
      await put(`/v1/admin/apps/${app.id}/store-waiver`, { waived: on, note: note.trim() });
      setNote('');
      setDone(
        on
          ? `${app.name} can now be published with an unfinished listing. Its team still has to press Publish.`
          : `The checklist applies to ${app.name} again. Anything already in the library stays there.`,
      );
      onChanged();
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Library checklist">
      <div className="row">
        {/* The same pill the list shows, from the same resolved fact — this panel is where somebody
            decides whether a waiver is worth granting, and "published" alone has misled them into
            granting one for a game that was never going to appear. */}
        <StorePill app={app} />
        {published ? (
          <>
            <span className="muted">·</span>
            <span className="muted">
              published <Time at={app.store.publishedAt ?? 0} />
            </span>
          </>
        ) : null}
        <span className="muted">·</span>
        <span className="muted">
          {app.store.publicBuilds} finished build{app.store.publicBuilds === 1 ? '' : 's'} on{' '}
          <span className="mono">public</span>
          {app.store.visibleBuilds !== app.store.publicBuilds
            ? `, ${app.store.visibleBuilds} past validation`
            : null}
        </span>
        {waived ? (
          <>
            <span className="muted">·</span>
            <span className="pill">
              waived <Time at={app.store.readinessWaivedAt ?? 0} />
            </span>
          </>
        ) : null}
      </div>

      {missing.length === 0 ? (
        <p className="note">Listing complete: the checklist refuses nothing.</p>
      ) : (
        <p className="note">
          Missing:{' '}
          {missing
            .map((item) => STORE_READINESS_LABELS[item as StoreReadinessItem] ?? item)
            .join(' ')}
        </p>
      )}

      <div className="row">
        <input
          type="text"
          style={{ minWidth: 240 }}
          placeholder="Why (required, kept in the audit log)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <button
          className={waived ? undefined : 'danger'}
          disabled={busy || note.trim() === ''}
          onClick={() => void set(!waived)}
        >
          {busy ? 'Saving…' : waived ? 'Apply the checklist again' : 'Waive the checklist'}
        </button>
      </div>

      {app.store.publicBuilds === 0 ? (
        <p className="note">
          <span className="warn">
            Nothing on <span className="mono">public</span>: a waiver alone lists a game with no build.
          </span>
        </p>
      ) : null}

      <p className="note">
        A waiver only lifts the refusal on the team&rsquo;s Publish button. It publishes nothing, and clearing it
        takes nothing out of the library.
      </p>

      {error ? <p className="error">{error}</p> : null}
      {done ? <p className="ok">{done}</p> : null}
    </Panel>
  );
}
